import React from 'react';
import { FileOutlined, LinkOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Container } from '@/components/ui/Container';
import { Button } from '@/components/ui/Button';
import emptyDescImg from '@/assets/images/empty_description.png';
import EmptyState from '@/components/ui/EmptyState';
import RichTextEditor from '@/components/ui/RichTextEditor';
import { Markdown } from '@/components/ui/Markdown';
import Link from '@/components/ui/Link';
import { 
    truncateFileName, 
    linkifyPlainUrlsForMarkdown,
    isLikelyImageAttachmentUrl,
    getAttachmentFileExtension,
    resolveAttachmentUrl
} from '@hooks/projects/projectHelpers';
import {
    DETAIL_ATTACH_URL_LINK_STYLE,
} from '@hooks/projects/detail/useProjectDetail';
import {
    EmptyStateBlock,
    EmptyStateIcon,
    EmptyStateTitle,
    EmptyStateDescription,
    PanelSectionLabel,
    AttachmentCardsRow,
} from '@/pages/inside/tenant/style';
import { PendingAttachmentTile } from '@/components/module/projects/sprints_tab/PendingRichTextAttachments';
import { LinkWrap } from '@/components/module/projects/detail_tab/ProjectAttachmentsPanel';
import TaskAttachmentTile from '@/components/module/projects/task/TaskAttachmentTile';

export default function ProjectDescriptionContent({
    hasContent,
    isEditingDetail,
    showDetailAndAttachmentActions,
    setIsEditingDetail,
    editContent,
    setEditContent,
    isInvitedCollaborator,
    mentionUsers,
    mentionMilestones,
    mentionSprints,
    handlePostFilesSelected,
    handleLinkRequest,
    pendingPostAttachments,
    removePendingPostAttachment,
    showProjectDetailsAsMarkdown,
    projectDetailsContentLinked,
    projectDetailsContent,
    detailsMarkdownAttachmentItems,
    handleMarkdownDescriptionLinkClick,
    pendingLinkAttachments = [],
    removePendingLinkAttachment,
    setPreviewImageUrl,
    descriptionDraft,
    handleResetDraft,
    deletedSavedUrls = new Set(),
    handleDeleteSavedAttachment,
}) {
    const savedLinks = React.useMemo(() => {
        return (detailsMarkdownAttachmentItems || [])
            .filter((item) => item.type === 'link' && !deletedSavedUrls.has(item.url));
    }, [detailsMarkdownAttachmentItems, deletedSavedUrls]);

    const savedFiles = React.useMemo(() => {
        return (detailsMarkdownAttachmentItems || [])
            .filter((item) => item.type === 'file' && !deletedSavedUrls.has(item.url));
    }, [detailsMarkdownAttachmentItems, deletedSavedUrls]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minHeight: 0, width: '100%' }}>
            {!hasContent && !isEditingDetail ? (
                <EmptyState
                    title="No description"
                    description={
                        showDetailAndAttachmentActions 
                            ? 'Add a project description to document key details, goals, and outline the scope of this project for your team.' 
                            : 'No description has been added to this project yet.'
                    }
                    image={emptyDescImg}
                    buttonText={showDetailAndAttachmentActions ? "Add Description" : undefined}
                    buttonIcon={<PlusOutlined style={{ fontSize: '14px' }} />}
                    onButtonClick={() => setIsEditingDetail(true)}
                    titleSize="20px"
                    descriptionSize="12px"
                    imageSize="220px"
                    containerPadding="60px 24px"
                    containerMinHeight="340px"
                />
            ) : isEditingDetail ? (
                <>
                    <RichTextEditor
                        value={editContent}
                        onChange={setEditContent}
                        placeholder="Add project detail (bold, lists, links). Type @ to mention someone, milestone, or sprint…"
                        showAttachButton={!isInvitedCollaborator}
                        mentionUsers={mentionUsers}
                        mentionMilestones={mentionMilestones}
                        mentionSprints={mentionSprints}
                        onFilesSelected={isInvitedCollaborator ? undefined : handlePostFilesSelected}
                        onLinkRequest={isInvitedCollaborator ? undefined : handleLinkRequest}
                        onAttachmentRemoved={(index) => {
                            const p = pendingPostAttachments[index];
                            if (p) removePendingPostAttachment(p.id);
                        }}
                    />
                    {((savedLinks.length > 0 || pendingLinkAttachments.length > 0) || 
                      (savedFiles.length > 0 || pendingPostAttachments.length > 0)) && (
                        <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {(savedLinks.length > 0 || pendingLinkAttachments.length > 0) && (
                                <div>
                                    <PanelSectionLabel
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            marginBottom: 8,
                                            fontSize: 11,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            opacity: 0.75,
                                        }}
                                    >
                                        <LinkOutlined style={{ fontSize: 12 }} />
                                        Links
                                    </PanelSectionLabel>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                        {savedLinks.map((l, idx) => (
                                            <LinkWrap key={`saved-link-${idx}`}>
                                                <Link
                                                    href={l.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={l.title || l.url}
                                                    style={{
                                                        ...DETAIL_ATTACH_URL_LINK_STYLE,
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        minWidth: 0,
                                                        flex: '1 1 auto',
                                                    }}
                                                >
                                                    {l.title || l.url}
                                                </Link>
                                                <button
                                                    type="button"
                                                    className="link-delete-btn"
                                                    aria-label="Remove link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleDeleteSavedAttachment(l);
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </LinkWrap>
                                        ))}
                                        {pendingLinkAttachments.map((l) => (
                                            <LinkWrap key={l.id}>
                                                <Link
                                                    href={l.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={l.title || l.url}
                                                    style={{
                                                        ...DETAIL_ATTACH_URL_LINK_STYLE,
                                                        display: 'block',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        minWidth: 0,
                                                        flex: '1 1 auto',
                                                    }}
                                                >
                                                    {l.title || l.url}
                                                </Link>
                                                <button
                                                    type="button"
                                                    className="link-delete-btn"
                                                    aria-label="Remove link"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        removePendingLinkAttachment(l.id);
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </LinkWrap>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(savedFiles.length > 0 || pendingPostAttachments.length > 0) && (
                                <div>
                                    <PanelSectionLabel
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            marginBottom: 8,
                                            fontSize: 11,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.04em',
                                            opacity: 0.75,
                                        }}
                                    >
                                        <FileOutlined style={{ fontSize: 12 }} />
                                        Attachments
                                    </PanelSectionLabel>
                                    <div
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                                            gap: 10,
                                        }}
                                    >
                                        {savedFiles.map((p, idx) => {
                                             const isImage = isLikelyImageAttachmentUrl(p.url);
                                             const ext = getAttachmentFileExtension(p.title || p.url || '');
                                             const resolvedUrl = p.url ? resolveAttachmentUrl(p.url) : '';
                                             return (
                                                 <TaskAttachmentTile
                                                     key={`saved-file-${idx}`}
                                                     href={resolvedUrl}
                                                     title={p.title || 'Saved File'}
                                                     isImage={isImage}
                                                     ext={ext}
                                                     onPreviewImage={(url) => setPreviewImageUrl?.(url)}
                                                     onOpenFile={(url) => {
                                                         if (resolvedUrl) {
                                                             window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
                                                         }
                                                     }}
                                                     onRemove={() => handleDeleteSavedAttachment(p)}
                                                 />
                                             );
                                         })}
                                        {pendingPostAttachments.map((p) => {
                                             const isImage = (p.type || '').startsWith('image/');
                                             const ext = getAttachmentFileExtension(p.name || '');
                                             return (
                                                 <TaskAttachmentTile
                                                     key={p.id}
                                                     href={p.preview}
                                                     title={p.name}
                                                     isImage={isImage}
                                                     ext={ext}
                                                     onPreviewImage={(url) => setPreviewImageUrl?.(url)}
                                                     onOpenFile={(url) => {
                                                         const fileUrl = p.preview || (p.file ? URL.createObjectURL(p.file) : '');
                                                         if (fileUrl) {
                                                             window.open(fileUrl, '_blank', 'noopener,noreferrer');
                                                         }
                                                     }}
                                                     onRemove={() => removePendingPostAttachment(p.id)}
                                                 />
                                             );
                                         })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </>
            ) : (
                <Container
                    $flex="1"
                    $minHeight="200px"
                    $overflow="auto"
                    $padding="12px 0"
                    className="project-detail-content"
                    style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
                >
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Markdown
                            source={showProjectDetailsAsMarkdown ? linkifyPlainUrlsForMarkdown(projectDetailsContent) : projectDetailsContentLinked}
                            linkPrimary
                            richInlineAnchors
                            attachmentItems={detailsMarkdownAttachmentItems}
                            onLinkClick={handleMarkdownDescriptionLinkClick}
                        />
                    </div>
                </Container>
            )}
        </div>
    );
}

